import type {
  BuilderSection,
  HeaderNavItem,
  WebsiteBuilderPage,
  WebsiteBuilderState,
} from 'types/websiteBuilder';

function slugify(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function normalizePageSlug(page: WebsiteBuilderPage): string {
  const direct = typeof page.slug === 'string' ? page.slug.trim() : '';
  if (direct) return direct.replace(/^\/+/, '');
  const fromTitle = slugify(page.title || '');
  if (fromTitle) return fromTitle;
  return `page-${page.id.slice(-6)}`;
}

function normalizeCompanyDomain(input: string | undefined): string {
  return (input ?? '').trim().toLowerCase();
}

function createPublicWebsiteHref(
  companyDomain: string | undefined,
  slug: string,
): string {
  const normalizedSlug = slug.replace(/^\/+/, '');
  const normalizedDomain = normalizeCompanyDomain(companyDomain);
  if (!normalizedDomain) return `/${normalizedSlug}`;
  return `/www/${normalizedDomain}/${normalizedSlug}`;
}

function createPageNavItems(
  pages: WebsiteBuilderPage[],
  companyDomain: string | undefined,
): HeaderNavItem[] {
  return pages.map((page) => ({
    sourcePageId: page.id,
    label: page.title?.trim() ? page.title : 'Untitled Page',
    href: createPublicWebsiteHref(companyDomain, normalizePageSlug(page)),
  }));
}

function readHeaderNavItems(section: BuilderSection): HeaderNavItem[] {
  const raw = section.data.navItems;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === 'object' && item != null,
    )
    .map((item) => ({
      label: typeof item.label === 'string' ? item.label : '',
      href: typeof item.href === 'string' ? item.href : '/',
      sourcePageId:
        typeof item.sourcePageId === 'string' ? item.sourcePageId : undefined,
    }));
}

/**
 * Keep header menu items in sync with created pages while preserving
 * manually-added static items (e.g., Home/About/Give links).
 */
export function syncHeaderNavigationWithPages(
  state: WebsiteBuilderState,
): WebsiteBuilderState {
  const pages = Array.isArray(state.pages) ? state.pages : [];
  const headerIndex = state.sections.findIndex(
    (section) => section.type === 'header',
  );
  if (headerIndex === -1) return state;

  const headerSection = state.sections[headerIndex];
  const existingNavItems = readHeaderNavItems(headerSection);
  const pageIdSet = new Set(pages.map((page) => page.id));
  const pageNavById = new Map(
    createPageNavItems(pages, state.companyDomain).map((item) => [
      item.sourcePageId as string,
      item,
    ]),
  );

  // Preserve only manual/static menu items that are not linked to pages.
  const staticNavItems = existingNavItems.filter(
    (item) =>
      item.sourcePageId == null ||
      item.sourcePageId.trim() === '' ||
      !pageIdSet.has(item.sourcePageId),
  );

  // Preserve current linked-page order from header nav (user drag/drop),
  // then append only newly-created linked pages that are not in nav yet.
  const linkedNavItemsInCurrentOrder = existingNavItems
    .filter((item) => {
      const pageId =
        typeof item.sourcePageId === 'string' ? item.sourcePageId.trim() : '';
      return pageId.length > 0 && pageIdSet.has(pageId);
    })
    .map((item) => pageNavById.get(item.sourcePageId as string))
    .filter((item): item is HeaderNavItem => item != null);

  const linkedNavIdsInCurrentOrder = new Set(
    linkedNavItemsInCurrentOrder
      .map((item) =>
        typeof item.sourcePageId === 'string' ? item.sourcePageId.trim() : '',
      )
      .filter((id) => id.length > 0),
  );

  const newlyLinkedNavItems = createPageNavItems(
    pages,
    state.companyDomain,
  ).filter(
    (item) =>
      typeof item.sourcePageId === 'string' &&
      !linkedNavIdsInCurrentOrder.has(item.sourcePageId),
  );

  const mergedNavItems = [
    ...staticNavItems,
    ...linkedNavItemsInCurrentOrder,
    ...newlyLinkedNavItems,
  ];

  const nextHeaderSection: BuilderSection = {
    ...headerSection,
    data: {
      ...headerSection.data,
      navItems: mergedNavItems,
    },
  };

  const nextSections = [...state.sections];
  nextSections[headerIndex] = nextHeaderSection;

  return {
    ...state,
    sections: nextSections,
  };
}
